import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HomeIcon, 
  ArrowPathIcon, 
  ExclamationTriangleIcon,
  BugAntIcon,
  ArrowLeftIcon 
} from '@heroicons/react/24/outline';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { SUPPORT_CONTACT } from '../../utils/constants';

const ServerErrorPage: React.FC = () => {
  const navigate = useNavigate();

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <Card className="p-8 text-center">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mb-6"
          >
            <BugAntIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-6xl font-bold text-gray-900 dark:text-white mb-4"
          >
            500
          </motion.h1>

          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-2xl font-semibold text-gray-900 dark:text-white mb-4"
          >
            Server Error
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-600 dark:text-gray-400 mb-8"
          >
            Something went wrong on our end. Our team has been notified 
            and we're working to fix this issue.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <Button
              variant="primary"
              fullWidth
              onClick={handleRefresh}
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Try Again
            </Button>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Go Back
              </Button>

              <Link to="/dashboard" className="flex-1">
                <Button variant="secondary" fullWidth>
                  <HomeIcon className="h-5 w-5 mr-2" />
                  Dashboard
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-center space-x-2 mb-4">
              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Error Reference: {Date.now().toString(36)}
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                What you can try:
              </p>
              <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <li>• Refresh the page in a few moments</li>
                <li>• Check your internet connection</li>
                <li>• Clear your browser cache</li>
                <li>• Contact support if the problem persists</li>
              </ul>
            </div>

            <div className="mt-4 space-x-4">
              <a
                href={`mailto:${SUPPORT_CONTACT.EMAIL}`}
                className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
              >
                Email Support
              </a>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <a
                href={`tel:${SUPPORT_CONTACT.PHONE}`}
                className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
              >
                Call Support
              </a>
            </div>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
};

export default ServerErrorPage;
